import { useEffect, useRef, useState } from "react"

export type NotificationItem = {
  id: number
  kind: string
  title: string
  message: string
  critical: boolean
  aggregateType: string
  aggregateId: number
  occurredAt: string
}

type FeedPayload = {
  notifications: NotificationItem[]
  latestId: number
}

type ConnectionMode = "realtime" | "polling"

/**
 * How the feed learns that something new exists.
 *
 * - `stream` opens an EventSource and falls back to polling when it fails. The
 *   diner has no other wire, so this stays its default.
 * - `push` opens nothing and fetches only when `pushSignal` changes. The staff
 *   app already holds an ActionCable subscription that carries every location
 *   event, so a second connection asking "anything yet?" was pure duplication:
 *   the SSE endpoint answers in four queries per eleven seconds and is closed
 *   for ten of them.
 * - `polling` is the honest fallback for `push` when that subscription is down.
 */
type FeedTransport = "stream" | "push" | "polling"

type UseNotificationFeedOptions = {
  feedPath: string
  streamPath: string
  enabled?: boolean
  maxItems?: number
  pollingIntervalMs?: number
  transport?: FeedTransport
  /**
   * Read in `push` mode only. Every change fetches incrementally; it never
   * re-opens or tears down anything, so the caller can pass a monotonic event
   * id straight from the wire. Zero means nothing has arrived yet.
   */
  pushSignal?: number
}

const DEFAULT_MAX_ITEMS = 20
const DEFAULT_POLLING_INTERVAL_MS = 7000

function mergeNotifications(
  current: NotificationItem[],
  incoming: NotificationItem[],
  maxItems: number,
): NotificationItem[] {
  const indexed = new Map<number, NotificationItem>()

  current.forEach((notification) => {
    indexed.set(notification.id, notification)
  })

  incoming.forEach((notification) => {
    indexed.set(notification.id, notification)
  })

  return Array.from(indexed.values())
    .sort((left, right) => {
      if (left.id === right.id) return 0
      return right.id - left.id
    })
    .slice(0, maxItems)
}

export function useNotificationFeed({
  feedPath,
  streamPath,
  enabled = true,
  maxItems = DEFAULT_MAX_ITEMS,
  pollingIntervalMs = DEFAULT_POLLING_INTERVAL_MS,
  transport = "stream",
  pushSignal = 0,
}: UseNotificationFeedOptions) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [connectionMode, setConnectionMode] = useState<ConnectionMode>("realtime")
  // Consumers that turn notifications into data refreshes need to tell the
  // first payload (history, already reflected in the page they are looking at)
  // from everything after it (news). An empty history is indistinguishable from
  // "not loaded yet" by the array alone, hence an explicit flag.
  const [initialized, setInitialized] = useState(false)
  const latestIdRef = useRef<number>(0)
  // Set by the effect below so the push effect can fetch without listing
  // `fetchFeed` as a dependency, which would tear down the transport on every
  // render.
  const fetchFeedRef = useRef<((incremental?: boolean) => Promise<void>) | null>(null)
  const lastPushRef = useRef<number>(0)
  const eventSourceRef = useRef<EventSource | null>(null)
  const pollingTimerRef = useRef<number | null>(null)
  const streamErrorCountRef = useRef<number>(0)
  const streamOpenedRef = useRef<boolean>(false)

  useEffect(() => {
    if (!enabled) {
      return undefined
    }

    let disposed = false

    // applyPayload and fetchFeed live inside the effect, not at hook scope, so
    // the dependency array can stay exhaustive. Hoisted, they were rebuilt on
    // every render, and listing them as dependencies would tear down and
    // reopen the EventSource on every render instead of only when the feed's
    // identity actually changes. Everything they close over is either a ref or
    // already a dependency.
    const applyPayload = (payload: FeedPayload | null) => {
      if (!payload || !Array.isArray(payload.notifications)) return

      const candidateLatest = Number(payload.latestId || 0)
      if (Number.isFinite(candidateLatest)) {
        latestIdRef.current = Math.max(latestIdRef.current, candidateLatest)
      }

      if (payload.notifications.length === 0) return

      setNotifications((current) =>
        mergeNotifications(current, payload.notifications, maxItems),
      )
    }

    const fetchFeed = async (incremental = true) => {
      const url = new URL(feedPath, window.location.origin)
      if (incremental && latestIdRef.current > 0) {
        url.searchParams.set("since_id", String(latestIdRef.current))
      }

      const response = await fetch(url.toString(), {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      })
      if (!response.ok) return

      const payload = (await response.json()) as FeedPayload
      applyPayload(payload)
    }

    const clearPolling = () => {
      if (!pollingTimerRef.current) return

      window.clearInterval(pollingTimerRef.current)
      pollingTimerRef.current = null
    }

    const startPolling = () => {
      if (pollingTimerRef.current) return

      setConnectionMode("polling")
      void fetchFeed(true)
      pollingTimerRef.current = window.setInterval(() => {
        void fetchFeed(true)
      }, pollingIntervalMs)
    }

    const stopStream = () => {
      if (!eventSourceRef.current) return

      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    const startStream = () => {
      const streamUrl = new URL(streamPath, window.location.origin)
      if (latestIdRef.current > 0) {
        streamUrl.searchParams.set("since_id", String(latestIdRef.current))
      }

      const source = new EventSource(streamUrl.toString())
      eventSourceRef.current = source

      source.onopen = () => {
        if (disposed) return

        streamOpenedRef.current = true
        streamErrorCountRef.current = 0
        clearPolling()
        setConnectionMode("realtime")
      }

      source.onmessage = (messageEvent) => {
        if (disposed) return

        try {
          const payload = JSON.parse(messageEvent.data) as FeedPayload
          applyPayload(payload)
        } catch {
          // Ignore malformed payloads and continue listening.
        }
      }

      source.onerror = () => {
        if (disposed) return

        if (!streamOpenedRef.current) {
          stopStream()
          startPolling()
          return
        }

        streamErrorCountRef.current += 1
        if (streamErrorCountRef.current < 3) return

        stopStream()
        startPolling()
      }
    }

    fetchFeedRef.current = fetchFeed

    void fetchFeed(false)
      .catch(() => {
        // `push` has no connection of its own to fall back FROM: a failed first
        // read is a failed request, not a dead transport, and the next wire
        // message will fetch again. Only `stream` and `polling` degrade here.
        if (transport !== "push") startPolling()
      })
      .finally(() => {
        if (disposed) return

        setInitialized(true)

        if (transport === "stream") {
          startStream()
        } else if (transport === "polling") {
          startPolling()
        }
      })

    return () => {
      disposed = true
      fetchFeedRef.current = null
      setInitialized(false)
      stopStream()
      clearPolling()
    }
  }, [feedPath, streamPath, enabled, maxItems, pollingIntervalMs, transport])

  // `push` mode's entire transport. Deliberately a separate effect: a signal
  // change must fetch, not reconnect.
  useEffect(() => {
    if (!enabled || transport !== "push") return
    if (pushSignal <= 0 || pushSignal === lastPushRef.current) return

    lastPushRef.current = pushSignal
    void fetchFeedRef.current?.(true)
  }, [enabled, transport, pushSignal])

  return {
    notifications,
    connectionMode,
    initialized,
  }
}
